import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { Button, IconButton } from '@mui/material';
import css from './DataTable.module.scss';
import {
  changeFilterOperator,
  dateFilterParts,
  dateFilterValue,
  getOperators,
  numericOperators,
  singleSelectOperators,
  stringOperators,
} from './filtering';
import type {
  GridColDef,
  GridFilterInputValueProps,
  GridFilterModel,
  GridFilterOperator,
} from './types';

export function FilterInput({
  item,
  applyValue,
  label = 'Filter value',
}: GridFilterInputValueProps & { label?: string }) {
  return (
    <input
      aria-label={label}
      placeholder={item.operator === 'isAnyOf' ? 'Values, separated by commas' : 'Enter a value'}
      className={css.input}
      value={Array.isArray(item.value) ? item.value.join(', ') : (item.value ?? '')}
      onChange={(event) =>
        applyValue({
          ...item,
          value:
            item.operator === 'isAnyOf'
              ? event.target.value.split(',').map((v) => v.trim())
              : event.target.value,
        })
      }
    />
  );
}
/**
 * A day, and for a `dateTime` column an optional time. Without a time the filter compares whole
 * days, so "is" still matches anything that day. The time waits for a day, since it means nothing
 * without one.
 */
function DateFilterInput({
  index,
  value,
  withTime,
  onChange,
}: {
  index: number;
  value: unknown;
  withTime: boolean;
  onChange: (value: string) => void;
}) {
  const { date, time } = dateFilterParts(value);
  const dateInput = (
    <input
      aria-label={`Filter ${index + 1} date`}
      className={css.input}
      type="date"
      value={date}
      onInput={(e) => onChange(dateFilterValue(e.currentTarget.value, time))}
    />
  );
  if (!withTime) return dateInput;
  return (
    <div className={css.dateTimeInput}>
      {dateInput}
      <input
        aria-label={`Filter ${index + 1} time (optional)`}
        title={date ? 'Optional. Leave empty to match the whole day.' : 'Pick a date first'}
        className={css.input}
        type="time"
        disabled={!date}
        value={time}
        onInput={(e) => onChange(dateFilterValue(date, e.currentTarget.value))}
      />
    </div>
  );
}
const withInput = (operators: GridFilterOperator[]) =>
  operators.map((op) => ({
    ...op,
    InputComponent: op.requiresFilterValue === false ? undefined : FilterInput,
  }));
export const getGridStringOperators = () => withInput(stringOperators());
export const getGridNumericOperators = () => withInput(numericOperators());
export const getGridSingleSelectOperators = singleSelectOperators;

export function FilterPanel({
  columns,
  model,
  onChange,
  onClose,
}: {
  columns: readonly GridColDef[];
  model: GridFilterModel;
  onChange: (model: GridFilterModel) => void;
  onClose: () => void;
}) {
  const filterable = columns.filter(
    (column) => column.filterable !== false && column.field !== '__check__',
  );
  const update = (index: number, item: GridFilterModel['items'][number]) =>
    onChange({ ...model, items: model.items.map((old, i) => (i === index ? item : old)) });
  return (
    <div className={css.filterPanel}>
      <div className={css.panelHeading}>
        <strong>
          <FilterAltOutlinedIcon fontSize="small" />
          Filters
        </strong>
        <IconButton aria-label="Close filters" onClick={onClose}>
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </div>
      <label className={css.logicLabel}>
        Match{' '}
        <select
          aria-label="Filter match mode"
          className={css.input}
          value={model.logicOperator ?? 'and'}
          onChange={(e) => onChange({ ...model, logicOperator: e.target.value as 'and' | 'or' })}
        >
          <option value="and">all conditions</option>
          <option value="or">any condition</option>
        </select>
      </label>
      {model.items.map((item, index) => {
        const column = columns.find((c) => c.field === item.field);
        if (!column) return null;
        const operators = getOperators(column);
        const selectedOperator = operators.find((op) => op.value === item.operator);
        const CustomInput = selectedOperator?.InputComponent;
        const options =
          column.type === 'boolean'
            ? [
                { value: true, label: 'Yes' },
                { value: false, label: 'No' },
              ]
            : column.valueOptions;
        const multiple = item.operator === 'isAnyOf';
        return (
          <div key={item.id ?? index} className={css.filterRow}>
            <label className={css.filterField}>
              <span>Column</span>
              <select
                className={css.input}
                aria-label={`Filter ${index + 1} column`}
                value={item.field}
                onChange={(e) => {
                  const next = filterable.find((c) => c.field === e.target.value)!;
                  update(index, {
                    id: item.id,
                    field: next.field,
                    operator: getOperators(next)[0].value,
                  });
                }}
              >
                {filterable.map((c) => (
                  <option key={c.field} value={c.field}>
                    {c.headerName || c.field}
                  </option>
                ))}
              </select>
            </label>
            <label className={css.filterField}>
              <span>Condition</span>
              <select
                className={css.input}
                aria-label={`Filter ${index + 1} operator`}
                value={item.operator}
                onChange={(e) => update(index, changeFilterOperator(item, e.target.value))}
              >
                {operators.map((op) => (
                  <option key={op.value} value={op.value}>
                    {op.label}
                  </option>
                ))}
              </select>
            </label>
            <div className={css.filterField}>
              <span>Value</span>
              {selectedOperator?.requiresFilterValue === false ? (
                <span className={css.noFilterValue}>No value needed</span>
              ) : CustomInput ? (
                <CustomInput item={item} applyValue={(next) => update(index, next)} />
              ) : options ? (
                <select
                  aria-label={`Filter ${index + 1} value`}
                  className={css.input}
                  multiple={multiple}
                  value={
                    multiple
                      ? Array.isArray(item.value)
                        ? item.value.map(String)
                        : []
                      : String(item.value ?? '')
                  }
                  onChange={(e) =>
                    update(index, {
                      ...item,
                      value: multiple
                        ? Array.from(e.target.selectedOptions, (o) => o.value)
                        : e.target.value,
                    })
                  }
                >
                  {!multiple && <option value="">Choose a value</option>}
                  {options.map((option) => {
                    const value = typeof option === 'object' ? option.value : option;
                    return (
                      <option key={String(value)} value={String(value)}>
                        {typeof option === 'object' ? option.label : String(option)}
                      </option>
                    );
                  })}
                </select>
              ) : column.type === 'date' || column.type === 'dateTime' ? (
                <DateFilterInput
                  index={index}
                  value={item.value}
                  withTime={column.type === 'dateTime'}
                  onChange={(value) => update(index, { ...item, value })}
                />
              ) : (
                <FilterInput
                  item={item}
                  label={`Filter ${index + 1} value`}
                  applyValue={(next) => update(index, next)}
                />
              )}
            </div>
            <IconButton
              aria-label={`Remove filter ${index + 1}`}
              onClick={() =>
                onChange({ ...model, items: model.items.filter((_, i) => i !== index) })
              }
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </div>
        );
      })}
      <div className={css.panelActions}>
        <Button
          startIcon={<AddRoundedIcon />}
          disabled={!filterable.length}
          onClick={() =>
            onChange({
              ...model,
              items: [
                ...model.items,
                {
                  id: crypto.randomUUID(),
                  field: filterable[0].field,
                  operator: getOperators(filterable[0])[0].value,
                },
              ],
            })
          }
        >
          Add filter
        </Button>
        <Button disabled={!model.items.length} onClick={() => onChange({ ...model, items: [] })}>
          Clear filters
        </Button>
      </div>
    </div>
  );
}
